import { useEffect, useMemo } from 'react';
import { useAnalytics } from './useAnalytics';
import type { Session, Store } from '../services/types';
import { useExperiment } from '../context/use-experiment';
import { Featureflag } from '../constants/featureFlags';
import { useChatContext } from '../context/use-chat-context';
import { getEnvBoolean } from '../../utils/env';

type UseIdentifySessionProps = {
  session?: Session;
  store?: Store;
};

const isAllFeatureFlagsEnabled = getEnvBoolean('ALL_FEATURE_FLAGS_ENABLED');

export const useIdentifySession = ({
  session,
  store,
}: UseIdentifySessionProps) => {
  const { identifySession } = useAnalytics();
  const {
    reloadAllFlagsForUser,
    getFeatureValue,
    isFlagEnabled,
    setFeaturesAvailable,
    setIsAvailableToCurrentUser,
  } = useExperiment();

  const { searchId, urlSearchQuery, enableSuperbarQuery } = useChatContext();

  const hasUrlParams = useMemo(() => {
    return searchId || urlSearchQuery || enableSuperbarQuery === 'true';
  }, [searchId, urlSearchQuery, enableSuperbarQuery]);

  useEffect(() => {
    if (!session || !store) return;

    const userProperties = {
      sessionId: session.id,
      storeId: store.id,
      storeUrl: store.url,
      storeName: store.name,
    };

    try {
      if (isAllFeatureFlagsEnabled) {
        setIsAvailableToCurrentUser(true);
        return;
      }

      const checkIfIsAvailableToCurrentUser = () => {
        if (hasUrlParams) return true;

        const visibilityFeatureFlagKey =
          store?.config?.config?.featureFlags?.visibilityFlag?.flagKey;
        const visibilityFeatureFlagVariant =
          store?.config?.config?.featureFlags?.visibilityFlag?.visibleOnVariant;

        return visibilityFeatureFlagKey?.length &&
          visibilityFeatureFlagVariant?.length
          ? getFeatureValue(visibilityFeatureFlagKey, '') ===
              visibilityFeatureFlagVariant
          : false;
      };

      const checkAvailableFlagsForCurrentUser = async () => {
        await reloadAllFlagsForUser(userProperties);

        const isAvailableToCurrentUser = checkIfIsAvailableToCurrentUser();

        setIsAvailableToCurrentUser(isAvailableToCurrentUser);

        const availableFlags = Object.entries(Featureflag)
          .filter(([_, value]) => {
            return isFlagEnabled(value as Featureflag, value);
          })
          .map((x) => x[1]);
        setFeaturesAvailable(availableFlags);
      };

      identifySession(session.id, userProperties);
      checkAvailableFlagsForCurrentUser();
    } catch (e) {
      console.error('Error identifying user:', e);
    }
  }, [
    session,
    store,
    identifySession,
    reloadAllFlagsForUser,
    getFeatureValue,
    setIsAvailableToCurrentUser,
    setFeaturesAvailable,
    isFlagEnabled,
    hasUrlParams,
  ]);
};
